import {
	Box,
	Button,
	Inline,
	CheckboxInput,
	Icons,
	Alert,
	Component,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
	Stack,
	Spinner,
} from "@sembark-travel/ui-base"
import { ButtonLink } from "@sembark-travel/ui-router"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { ShareViaWhatsApp } from "@sembark-travel/ui-share"
import {
	utcTimestampToLocalDate,
	formatDate,
	getDiff,
	localOrUtcTimestampToLocalDate,
	addUnit,
	humanizeDuration,
} from "@sembark-travel/datetime-utils"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import { useXHR } from "@sembark-travel/xhr"
import {
	SendTripWhatsAppDialog,
	type TSendTripWhatsAppAttachment,
} from "./WhatsApp"
import { TAllocation, useTripWhatsApp } from "./store"
import { useCanSendOnWhatsApp } from "../WhatsAppMessages"
import pluralize from "pluralize"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuthUser, useHasFeatureFlag } from "../Auth"
import { collect } from "../utils"
import {
	getMultiplierForPerComponentPricing,
	getDaywiseTransportsAndActivitiesForQuote,
	getMultiplierForOverallPerPersonPricing,
	getUniqueHotels,
	shouldAllowQuoteActions,
	tempDisableQuoteActionsMessageAlert,
	groupPricesPerPersonByService,
	allocatePax,
} from "./utils"
import {
	filterHotelNotes,
	IItinerary,
	IQuote,
	IQuoteCab,
	ITrip,
	ITripQuote,
	TComponentKey,
	TPricePerTravelComponentAllocation,
	TPricePerPersonAllocation,
} from "./store"
import { generatePath, useNavigate } from "../router-utils"
import {
	addMoneyFromDifferentCurrencies,
	addMoney,
	subtractMoney,
	makeCurrencyConverter,
	formatMoneyByIntl,
	moneyParseByDecimal,
	multipleMoneyBy,
	TMoney,
	divideMoneyBy,
	roundMoney,
	formatMoneyByDecimal,
} from "@sembark-travel/money"
import {
	EmailContent,
	TEmailComposer,
	TEmailInclusionOptions,
} from "../shared/EmailContent"
import { Required } from "utility-types"
import { ErrorBoundary } from "@sembark-travel/ui-error-boundary"
import {
	TTripQuoteEmailPdfWATemplate,
	useTripQuoteWhatsAppTemplate,
} from "../EmailPdfTemplates"
import { isTruthy } from "@sembark-travel/ui-form"
import { useSynedStorageState } from "../storage"
import { TTripQuotePdfTemplate } from "../EmailPdfTemplates/store"
import useSWR from "swr"
import { FLIGHT_TYPE } from "../Flights"
import {
	getEntryTypeDisplayName,
	getPurposeOfVisaDisplayName,
	visaProcessingTimeStr,
} from "../Visa"
import { TTripDestination } from "../TripDestinations"

export function ShareQuoteInDialog({
	quote: tripQuote,
	isValidating,
	isGenerating,
	id,
	refetchQuote,
}: {
	quote: ITripQuote
	refetchQuote: () => void
	isValidating?: boolean
	isGenerating?: boolean
	id?: number
}) {
	const xhr = useXHR()
	const [activeTab, setActiveTab] = useState<undefined | TShareVia>(undefined)
	const [refreshInterval, setRefreshInterval] = useState(5000)

	const refetchQuoteRef = useRef(refetchQuote)
	refetchQuoteRef.current = refetchQuote

	const shouldAllowQuoteShare = shouldAllowQuoteActions(
		tripQuote.pricing_strategy
	) //temp condition

	const { data } = useSWR(
		isGenerating && id ? `/itinerary-generation-status/${id}` : null,
		(key) =>
			xhr
				.get<{
					data: {
						done: boolean
						in_progress: boolean
						failed: boolean
					}
				}>(key)
				.then((resp) => {
					if (!resp.data.data.done) {
						setRefreshInterval((interval) => interval + 1000)
					}
					return resp.data.data
				}),
		{
			refreshInterval: isGenerating ? refreshInterval : 0,
		}
	)

	useEffect(() => {
		if (data) {
			if (data.in_progress === false && isGenerating) {
				refetchQuoteRef.current()
			}
		}
	}, [data, isGenerating])

	if (
		!tripQuote.quote ||
		!tripQuote.itinerary ||
		!tripQuote.given_quote ||
		!tripQuote.trip
	) {
		const editItineraryUrl = generatePath(
			"/trips/:tripId/quotes/:quoteId/edit-itinerary",
			{
				tripId: tripQuote.trip_id.toString(),
				quoteId: `${tripQuote.id}`,
			}
		)
		return (
			<Component initialState={false}>
				{({ state, setState }) => (
					<>
						{shouldAllowQuoteShare ? (
							<Button
								data-testid="share_quote"
								status="warning"
								disabled={isValidating}
								onClick={() => {
									setState(true)
								}}
							>
								{isValidating ? <Icons.Refresh spin /> : <Icons.Share />} Share
							</Button>
						) : (
							<Button
								onClick={tempDisableQuoteActionsMessageAlert}
								status="warning"
							>
								<Icons.Share /> Share
							</Button>
						)}
						<Dialog
							open={state}
							onClose={() => setState(false)}
							title="Share Quote"
							sm
						>
							<Dialog.Body>
								<Inline gap="4">
									<Box>
										<Icons.Attention size="8" color="warning" />
									</Box>
									<Box>
										<Stack gap="2">
											<Text fontSize="md" fontWeight="semibold" color="warning">
												Inclusions/Exclusions or Itinerary is missing from the
												quote.
											</Text>
											<Text>
												You can add or update inclusions/exclusions for a quote
												from the bottom on every quote. You can also click on
												the button below to add the required details.
											</Text>
											<Box>
												<ButtonLink
													to={editItineraryUrl}
													level="primary"
													status="primary"
													onClick={() => {
														setState(false)
													}}
												>
													Add Inclusions/Exclusions
												</ButtonLink>
											</Box>
										</Stack>
									</Box>
								</Inline>
							</Dialog.Body>
							<Dialog.Footer>
								<Button onClick={() => setState(false)}>Cancel</Button>
							</Dialog.Footer>
						</Dialog>
					</>
				)}
			</Component>
		)
	}

	return (
		<>
			{shouldAllowQuoteShare ? (
				<Button
					data-testid="share_quote"
					level="secondary"
					status="primary"
					onClick={() => {
						setActiveTab("whatsapp")
					}}
				>
					<Icons.Share /> Share
				</Button>
			) : (
				<Button onClick={tempDisableQuoteActionsMessageAlert} status="warning">
					<Icons.Share /> Share
				</Button>
			)}
			<Dialog
				open={Boolean(activeTab)}
				lg
				onClose={() => setActiveTab(undefined)}
				title="Share Package"
			>
				<ErrorBoundary>
					<ShareQuote
						activeTab={activeTab}
						setActiveTab={setActiveTab}
						quote={
							tripQuote as Required<
								ITripQuote,
								"quote" | "given_quote" | "trip" | "itinerary"
							>
						}
						isValidating={isValidating}
					/>
				</ErrorBoundary>
			</Dialog>
		</>
	)
}

type TShareVia = "whatsapp" | "text" | "rich_text" | "partner_sharing"

type TAllocationsByOption = Record<
	string | number,
	{
		allocation: ReturnType<typeof allocatePax>["allocation"]
		warnings: Array<string>
	}
>

function makePackageCurrencyConverterForTripQuote(
	tripQuote: ITripQuote,
	componentKey?: TComponentKey
) {
	const currencyPairs = tripQuote.given_quote?.currency_pairs
	const packageCurrency =
		tripQuote.given_quote?.given_currency || tripQuote.currency
	const convertCurrency = makeCurrencyConverter(currencyPairs || [])

	// Determine if we should use component-specific multiplier
	const pricingStrategy = tripQuote.pricing_strategy
	const isPerComponentPricingStrategy =
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"

	const shouldGetMultipliedByComponentMultiplier = isPerComponentPricingStrategy
		? true
		: componentKey === "flights" &&
			  (pricingStrategy === "overall_v2" ||
					pricingStrategy === "per_person_v2")
			? true
			: false

	// Use component-specific multiplier for new strategies, global for old strategies(v1/v2 overall, per person pricing strategies)
	const multiplier =
		shouldGetMultipliedByComponentMultiplier && componentKey
			? getMultiplierForPerComponentPricing(tripQuote, componentKey)
			: getMultiplierForOverallPerPersonPricing(tripQuote)

	return function convertMoneyToPackageCurrency(
		money: TMoney | Array<TMoney>,
		dontAddMarkup = false
	): TMoney {
		const amountInPackageCurrency = convertCurrency(
			packageCurrency,
			convertCurrency(tripQuote.currency, money)
		)
		if (dontAddMarkup) return amountInPackageCurrency
		return multipleMoneyBy(amountInPackageCurrency, multiplier)
	}
}

function ShareQuote({
	activeTab,
	setActiveTab,
	quote: tripQuote,
	isValidating,
}: {
	activeTab: undefined | TShareVia
	setActiveTab: (value: TShareVia | undefined) => void
	quote: Required<ITripQuote, "quote" | "given_quote" | "itinerary" | "trip">
	isValidating?: boolean
}) {
	const canShareWithPartner = useHasFeatureFlag("quote_partner_sharing")
	const xhr = useXHR()
	const navigate = useNavigate()
	const tripQuoteId = tripQuote.id
	const { itinerary, trip } = tripQuote
	const tripQuotePdfTemplateSettings = useTripQuoteWhatsAppTemplate()

	const { terms_and_conditions } = itinerary

	const [emailSubject, setEmailSubject] = useState("")
	const [hasCustomSubject, setHasCustomSubject] = useState(false)
	const fetchEmailContent = useCallback(
		async (params: object) => {
			return xhr
				.get<
					{
						content: string
						options: TEmailInclusionOptions
						has_custom_subject?: boolean
					} & Partial<TEmailComposer>
				>(`/trip-quotes/${tripQuoteId}/as-email`, {
					params: { ...params, _composer: 1 },
				})
				.then((resp) => {
					setEmailSubject(resp.data.subject || "")
					setHasCustomSubject(Boolean(resp.data.has_custom_subject))
					return resp.data
				})
		},
		[tripQuoteId, xhr]
	)

	return (
		<>
			{terms_and_conditions?.deleted_at ? (
				<Alert
					status="warning"
					title="Disabled Terms and Conditions"
					rounded="none"
				>
					TnC used in this quote has been disabled. Please edit
					Inclusions/Exclusions and select valid terms and conditions before
					sharing.
				</Alert>
			) : null}
			<Tabs layout="col">
				<TabsList>
					<TabItem
						active={activeTab === "whatsapp"}
						onClick={() => setActiveTab("whatsapp")}
					>
						WhatsApp
					</TabItem>
					<TabItem
						active={activeTab === "rich_text"}
						onClick={() => setActiveTab("rich_text")}
					>
						Email
					</TabItem>
					{canShareWithPartner &&
					(tripQuote.created_after_conversion ||
						tripQuote.used_for_conversion) ? (
						<TabItem
							active={activeTab === "partner_sharing"}
							onClick={() => setActiveTab("partner_sharing")}
						>
							Partner
						</TabItem>
					) : null}
				</TabsList>
				<TabContent active={activeTab === "whatsapp"}>
					<WhatsappContent
						quote={tripQuote}
						inclusionOptionsCacheKey={`trip_source_${String(
							trip?.trip_source.id || ""
						)}_quote_whatsapp_share_inclusions`}
						isValidating={isValidating || !tripQuotePdfTemplateSettings}
						tripQuotePdfTemplateSettings={tripQuotePdfTemplateSettings}
					/>
				</TabContent>
				<TabContent active={activeTab === "rich_text"} position="relative">
					{hasCustomSubject ? (
						<CopyToClipboard<HTMLDivElement>>
							{({ nodeToCopy, copyNodeTextToClipboard }) => (
								<Stack paddingX="4" paddingTop="2" gap="1">
									<Inline justifyContent="between" alignItems="center">
										<Text fontWeight="semibold">Subject</Text>
										<CopyToClipboardButton
											size="sm"
											level="primary"
											onClick={async () => {
												return copyNodeTextToClipboard()
											}}
										>
											<Icons.ClipboardCopy /> Copy Subject
										</CopyToClipboardButton>
									</Inline>
									<Box
										paddingY="2"
										paddingX="4"
										borderWidth="1"
										rounded="md"
										bgColor="subtle"
									>
										<Box ref={nodeToCopy}>{emailSubject || "..."}</Box>
									</Box>
								</Stack>
							)}
						</CopyToClipboard>
					) : null}
					<Box paddingX="4" paddingY="2">
						<Text fontSize="sm">
							<Icons.LightBulb opacity="70" /> Use toggles to customize the
							content according to your needs.
						</Text>
					</Box>
					<Box style={{ minHeight: "90vh" }}>
						{activeTab === "rich_text" ? (
							<EmailContent
								paddingY="4"
								fetchContent={fetchEmailContent}
								contentCacheKey={`/trip-quotes/${tripQuote.id}/as-email`}
								inclusionOptionsCacheKey={`trip_source_${String(
									trip?.trip_source.id || ""
								)}_quote_email_share_inclusions`}
								copyLabel="Copy"
								copyTrackId="quotes"
								downloadUrl={`/trip-quotes/${tripQuote.id}/as-email`}
								sendEmailUrl={`/trip-quotes/${tripQuote.id}/send-as-email`}
								sendEmailTitle="Send Quote Email"
								sendEmailSuccessMessage="Quote email queued for sending."
								onEmailSent={() =>
									navigate("/trips/:tripId/emails", {
										params: {
											tripId: String(trip.id),
										},
									})
								}
							/>
						) : null}
					</Box>
				</TabContent>
				<TabContent
					active={activeTab === "partner_sharing"}
					position="relative"
				>
					<Box paddingX="4" paddingY="2">
						<Text fontSize="sm">
							<Icons.LightBulb opacity="70" /> Use toggles to customize the
							content according to your needs.
						</Text>
					</Box>
					<Box style={{ minHeight: "90vh" }}>
						{activeTab === "partner_sharing" ? (
							<PartnerSharing quote={tripQuote} />
						) : null}
					</Box>
				</TabContent>
			</Tabs>
		</>
	)
}

function getContentForHotels(
	quote: IQuote,
	config: {
		includePrice: boolean
		showServiceWise?: boolean
		convertMoneyToPackageCurrency: (money: TMoney | Array<TMoney>) => TMoney
		similarWordInHotel?: boolean
		includeHotelNotes?: boolean
		given_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		given_currency?: string
		destinations?: TTripDestination[]
		priceBreakupStrategy: TPriceBreakupStrategy
		tripQuote: ITripQuote
		allocation?: TAllocation[]
	}
) {
	const { hotels, hotel_extras } = quote
	const {
		includePrice,
		showServiceWise = true,
		convertMoneyToPackageCurrency,
		similarWordInHotel,
		given_prices_per_component,
		given_currency,
		includeHotelNotes,
		destinations,
		priceBreakupStrategy,
	} = config
	const hotelNotes = filterHotelNotes({
		hotels,
		excludeInternalNotes: true,
		tripDestination: destinations,
	})

	return {
		get whatsApp() {
			return !hotels?.length && !hotel_extras?.length
				? ``
				: `🏨  *_Hotels_*
-----------
${getUniqueHotels(hotels, config)
	.map(
		({ hotel, nights, roomsAndMealPlans, similar_hotel_options }) => `*${nights
			.map((n) =>
				withOrdinalSuffix(
					getDiff(n, utcTimestampToLocalDate(quote.start_date), "days") + 1
				)
			)
			.join(", ")} ${pluralize("Night", nights.length)}* _at_ *${collect(
			[hotel]
				.concat(
					similar_hotel_options ? similar_hotel_options.map((s) => s.hotel) : []
				)
				.map((hotel) => hotel.location.short_name)
		)
			.unique()
			.toArray()
			.join("/")}*
_Check-in: ${formatDate(nights[0], "DD MMM")}_ & _Check-out: ${formatDate(addUnit(nights[nights.length - 1], 1, "day"), "DD MMM")}_
*${similarWordInHotel ? `${hotel.name} / Similar` : hotel.name}*${hotel.stars_string ? ` (${hotel.stars_string})` : ``}
${
	roomsAndMealPlans.length === 1
		? `${[
				roomsAndMealPlans[0].meal_plan.description,
				`${roomsAndMealPlans[0].no_of_rooms} ${roomsAndMealPlans[0].room_type.display_name}` +
					` (${roomsAndMealPlans[0].room_configuration?.description})`,
			]
				.filter(Boolean)
				.join(" • ")}${
				includePrice && showServiceWise
					? getContentForHotelRoomPrice({
							roomsAndMealPlan: roomsAndMealPlans[0],
							noOfNights: nights.length,
							convertMoneyToPackageCurrency,
						})
					: ""
			}`
		: roomsAndMealPlans
				.map(
					(r) =>
						`- *${r.nights
							.map((n) =>
								withOrdinalSuffix(
									getDiff(
										n,
										utcTimestampToLocalDate(quote.start_date),
										"days"
									) + 1
								)
							)
							.join(",")} ${pluralize("Night", r.nights.length)}:* ${
							r.meal_plan.description
								? `${r.meal_plan.description}, `
								: `${r.meal_plan.name}, `
						} ${r.no_of_rooms} ${r.room_type.display_name} (${
							r.room_configuration?.description
						})${
							includePrice && showServiceWise
								? getContentForHotelRoomPrice({
										roomsAndMealPlan: r,
										noOfNights: r.nights.length,
										convertMoneyToPackageCurrency,
									})
								: ""
						}`
				)
				.join("\n\n")
}${
			similar_hotel_options?.length
				? `\n*Similar Options:*\n\`\`\`-\`\`\` ` +
					similar_hotel_options
						.map(
							(option) =>
								`*${option.hotel.name}*${
									option.hotel.stars_string
										? ` (${option.hotel.stars_string})`
										: ``
								} ${
									option.hotel.location.name !== hotel.location.name
										? `\n\`\`\`•\`\`\` ${option.hotel.location.short_name}`
										: ``
								}${
									roomsAndMealPlans.length === 1 &&
									(roomsAndMealPlans[0].room_type.id !== option.room_type.id ||
										roomsAndMealPlans[0].no_of_rooms !== option.no_of_rooms)
										? `\n\`\`\`•\`\`\` ${option.no_of_rooms} ${option.room_type.display_name} (${roomsAndMealPlans[0].room_configuration?.description})`
										: ``
								}`
						)
						.join(`\n\`\`\`-\`\`\` `)
				: ``
		}`
	)
	.join("\n\n")}${
						hotel_extras?.length
							? `

*Hotel Special Inclusions*
-------
${hotel_extras
	.map(
		(ex) =>
			`${
				ex.date
					? `*${
							quote.nights < 1
								? formatDate(
										localOrUtcTimestampToLocalDate(
											quote.start_date_local,
											quote.start_date
										),
										"DD MMM"
									)
								: `${withOrdinalSuffix(
										getDiff(
											utcTimestampToLocalDate(ex.date),
											utcTimestampToLocalDate(quote.start_date),
											"days"
										) + 1
									)} Night`
						}* - `
					: ``
			}*${ex.service.name}* (${similarWordInHotel ? `${ex.hotel.name} / Similar` : ex.hotel.name})${
				includePrice && ex.given_price && showServiceWise
					? `
  • *${formatMoneyByIntl(
		convertMoneyToPackageCurrency(
			moneyParseByDecimal(ex.given_price, ex.currency)
		),
		{ showCurrency: true, roundOff: true, showSolidus: true }
	)}*`
					: ``
			}`
	)
	.join("\n")}`
							: ``
					}
${
	includeHotelNotes && hotelNotes && hotelNotes.length
		? `
🏨 *Notes*
------------------------
${hotelNotes
	.map(({ hotel, notes }) => {
		return `*${hotel}*
${notes
	.map(({ key, descriptions }) => {
		const descList = Object.values(descriptions)
			.map((desc) => `   ${desc}`)
			.join("\n")
		return `${key === "Hotel Note" ? `` : `  `}${key === "Hotel Note" ? `` : `*${key} :*\n`}${descList}`
	})
	.join("\n")}`
	})
	.join("\n\n")}`
		: ``
}${
						includePrice &&
						["only_hotel_per_person", "components_per_person"].includes(
							priceBreakupStrategy
						)
							? `${getContentForPerPerson({ given_currency, config })}`
							: ""
					}${
						includePrice
							? (() => {
									// Try to get prices from given prices per components, Incase the quote is created with advance pricing strategies
									const hotelsPrice =
										given_prices_per_component?.find(
											(comp) => comp.id === "hotels"
										)?.price || 0

									if (
										hotelsPrice &&
										given_currency &&
										Number(hotelsPrice || 0) > 0
									) {
										return `

*Total Hotels Price: ${formatMoneyByIntl(
											moneyParseByDecimal(hotelsPrice, given_currency),
											{
												roundOff: true,
												showCurrency: true,
												showSolidus: true,
											}
										)}*`
									}

									// Fallback to original calculation for backward compatibility, Incase the quote is created with old pricing (overall, per person) strategies.
									const processedHotels = getUniqueHotels(hotels, config)
									const hotelPricesFromProcessedData = processedHotels.flatMap(
										({ roomsAndMealPlans }) =>
											roomsAndMealPlans.map(
												({ given_price, currency, nights }) =>
													moneyParseByDecimal(
														(given_price || 0) * nights.length,
														currency || "INR"
													)
											)
									)
									return `

*Total Hotels Price: ${formatMoneyByIntl(
										convertMoneyToPackageCurrency(
											addMoneyFromDifferentCurrencies(
												hotelPricesFromProcessedData.concat(
													(hotel_extras || []).map(
														({ given_price, currency }) =>
															moneyParseByDecimal(given_price || 0, currency)
													)
												)
											)
										),
										{
											roundOff: true,
											showCurrency: true,
											showSolidus: true,
										}
									)}*`
								})()
							: ``
					}`
		},
	}
}

function getContentForHotelRoomPrice({
	roomsAndMealPlan,
	noOfNights,
	convertMoneyToPackageCurrency,
}: {
	roomsAndMealPlan: ReturnType<
		typeof getUniqueHotels
	>[number]["roomsAndMealPlans"][number]
	noOfNights: number
	convertMoneyToPackageCurrency: (money: TMoney | Array<TMoney>) => TMoney
}) {
	if (!roomsAndMealPlan.given_price) return ``
	function formatToPackageCurrency(
		amount: number,
		config?: Parameters<typeof formatMoneyByIntl>[1]
	): string {
		return formatMoneyByIntl(
			convertMoneyToPackageCurrency(
				moneyParseByDecimal(amount, roomsAndMealPlan.currency || "INR")
			),
			{
				roundOff: true,
				...config,
			}
		)
	}
	const perRoomPrices = [
		roomsAndMealPlan.per_room_given_price
			? `${formatToPackageCurrency(
					roomsAndMealPlan.per_room_given_price
				)} Per Room ${roomsAndMealPlan.room_configuration?.per_room_details}`
			: "",
		roomsAndMealPlan.per_adult_with_extra_bed_given_price
			? `${formatToPackageCurrency(
					roomsAndMealPlan.per_adult_with_extra_bed_given_price
				)} Per Adult with Extra Bed/Mattress ${
					roomsAndMealPlan.room_configuration?.per_adult_with_extra_bed_details
				}`
			: "",
		roomsAndMealPlan.per_child_with_extra_bed_given_price
			? `${formatToPackageCurrency(
					roomsAndMealPlan.per_child_with_extra_bed_given_price
				)} Per Child with Extra Bed/Mattress ${
					roomsAndMealPlan.room_configuration?.per_child_with_extra_bed_details
				}`
			: "",
		roomsAndMealPlan.per_child_without_extra_bed_given_price
			? `${formatToPackageCurrency(
					roomsAndMealPlan.per_child_without_extra_bed_given_price
				)} Per Child without Extra Bed/Mattress ${
					roomsAndMealPlan.room_configuration
						?.per_child_without_extra_bed_details
				}`
			: "",
	].filter((str): str is string => Boolean(str))
	return `
*Prices:* ${
		perRoomPrices.length
			? `
• ${perRoomPrices.join("\n• ")}
= *Total:* `
			: ``
	}*${formatToPackageCurrency(roomsAndMealPlan.given_price, {
		showCurrency: true,
	})}${
		noOfNights > 1
			? ` x ${noOfNights}N =  ${formatToPackageCurrency(
					roomsAndMealPlan.given_price * noOfNights,
					{ showSolidus: true }
				)}`
			: ``
	}*`
}

function checkCarsDisplayName(cabs: IQuoteCab[]) {
	const data = cabs.filter((cab) => cab.cab_type.display_name)
	return data.length
		? `_(${data.map((c) => `${c.no_of_cabs}-${c.cab_type.display_name}`).join(" + ")})_`
		: ""
}

function getContentForCabs(
	quote: IQuote,
	config: {
		includePrice: boolean
		showServiceWise?: boolean
		convertMoneyToPackageCurrency: (money: TMoney | TMoney[]) => TMoney
		given_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		given_currency?: string
		tripQuote: ITripQuote
		priceBreakupStrategy: TPriceBreakupStrategy
		groupedAllocations: TGroupedAllocation[]
	},
	templateSettings?: TTripQuoteEmailPdfWATemplate["transport_and_activity_section_settings"]
) {
	const { start_date, start_date_local } = quote
	const title =
		templateSettings?.title ||
		`Transportation${quote.travel_activities.length ? " and Activities" : ""}`

	const {
		includePrice,
		showServiceWise = false,
		convertMoneyToPackageCurrency,
		given_prices_per_component,
		given_currency,
		tripQuote,
		priceBreakupStrategy,
	} = config

	// Pricing Strategy
	const { pricing_strategy } = tripQuote

	const isPerComponentPricingStrategy =
		pricing_strategy === "per_component_v2" ||
		pricing_strategy === "per_component_per_person_v2"

	// component-specific converters for new advance pricing strategies
	const cabsConverter = makePackageCurrencyConverterForTripQuote(
		tripQuote,
		"cabs"
	)

	const activitiesConverter = makePackageCurrencyConverterForTripQuote(
		tripQuote,
		"activities"
	)

	const extrasConverter = makePackageCurrencyConverterForTripQuote(
		tripQuote,
		"extras"
	)

	const { daywiseServices, extraServiceWithoutDates } =
		getDaywiseTransportsAndActivitiesForQuote({
			transports: quote.cabs,
			transportExtras: quote.transport_extras,
			travelActivities: quote.travel_activities,
		})
	return {
		get whatsApp() {
			return !daywiseServices.length && !extraServiceWithoutDates.length
				? ``
				: `
🚖  *${title}*
-----------
${daywiseServices
	.map(({ date, date_local, transportsAndActivities, extras }) =>
		[
			`*${withOrdinalSuffix(
				getDiff(
					localOrUtcTimestampToLocalDate(date_local, date),
					localOrUtcTimestampToLocalDate(start_date_local, start_date),
					"day"
				) + 1
			)} Day - ${formatDate(localOrUtcTimestampToLocalDate(date_local, date), "ddd, Do MMM'YY")}*`,
		]
			.concat(
				transportsAndActivities.map(({ type, transport, activity }) => {
					const transportStartTimeLabel = transport?.start_time_formatted
						? `Start: ${transport.start_time_formatted}`
						: null
					const transportDurationLabel = transport?.duration_string
						? `Duration: ${transport.duration_string}`
						: null
					const transportScheduleInfoString =
						transportStartTimeLabel || transportDurationLabel
							? `\n   _${[transportStartTimeLabel, transportDurationLabel].filter(Boolean).join(" | ")}_`
							: ""
					const activityStartTimeLabel = activity?.start_time_formatted
						? `Start: ${activity.start_time_formatted}`
						: null
					const activityDurationLabel = activity?.duration_string
						? `Duration: ${activity.duration_string}`
						: null
					const activityScheduleInfoString =
						activityStartTimeLabel || activityDurationLabel
							? `\n   _${[activityStartTimeLabel, activityDurationLabel].filter(Boolean).join(" | ")}_`
							: ""
					return type === "transport"
						? `${transport.transport_service.name} ${checkCarsDisplayName(transport.cabs)}${
								includePrice && showServiceWise
									? `\n  • *Price: ${formatMoneyByIntl(
											cabsConverter(
												addMoneyFromDifferentCurrencies(
													transport.cabs.map(({ given_price, currency }) =>
														moneyParseByDecimal(given_price || 0, currency)
													)
												)
											),
											{ roundOff: true, showCurrency: true }
										)}*`
									: ""
							}${"   "}${transportScheduleInfoString}`
						: `${activity.activity.name}${
								activity.ticket_type
									? ` - ${activity.ticket_type.display_name}`
									: ``
							} _(${
								activity.ticket_tourist_configurations_short_name ||
								activity.ticket_tourist_configurations
									.map(
										({ configuration, quantity }) =>
											`${quantity} ${configuration.name}`
									)
									.join(" + ")
							})_${
								includePrice && showServiceWise
									? `\n  • *Price: ${formatMoneyByIntl(
											activitiesConverter(
												addMoneyFromDifferentCurrencies(
													activity.ticket_tourist_configurations.map(
														({ given_price, currency }) =>
															moneyParseByDecimal(given_price || 0, currency)
													)
												)
											),
											{ roundOff: true, showCurrency: true }
										)}*`
									: ""
							}${activityScheduleInfoString}`
				})
			)
			.concat(
				extras.map(
					({ service, given_price, currency }) =>
						`${service.name}${
							includePrice && showServiceWise && given_price
								? `\n  • *Price: ${formatMoneyByIntl(
										extrasConverter(moneyParseByDecimal(given_price, currency)),
										{ roundOff: true, showCurrency: true }
									)}*`
								: ""
						}`
				)
			)
			.join("\n• ")
	)
	.join("\n\n")}${
						extraServiceWithoutDates.length
							? `

---
*Other Inclusions*
${extraServiceWithoutDates
	.map(
		({ service, given_price, currency }) =>
			`${service.name}${
				includePrice && showServiceWise && given_price
					? `\n  • *Price: ${formatMoneyByIntl(
							extrasConverter(moneyParseByDecimal(given_price, currency)),
							{ roundOff: true, showCurrency: true }
						)}*`
					: ""
			}`
	)
	.join("\n• ")}`
							: ``
					}${
						includePrice &&
						["components_per_person"].includes(priceBreakupStrategy)
							? `${getContentPerPersonOptionWise({ given_currency, config, show: "cabs", groupedAllocations: config?.groupedAllocations })}`
							: ""
					}${
						includePrice
							? `\n\n*Total Transportation and Activities Price: ${(() => {
									if (isPerComponentPricingStrategy) {
										// Under advanced per-component strategies, the finalized "extras" component total
										// includes both transport extras and other extras. Since this section represents
										// Transport + Activities, we only add transport extras here, priced with the
										// extras multiplier. The Other Extras section will account for non-transport extras.

										// Sum of Transport Extras -> we are using extras component multiplier because during advance pricing strategy user applied markup tax for combined extras (Transport Extras + Other Extras)
										const transportExtrasMoney = extrasConverter(
											addMoneyFromDifferentCurrencies(
												quote.transport_extras.map(
													({ given_price, currency }) =>
														moneyParseByDecimal(given_price || 0, currency)
												)
											)
										)
										const cabsPrice =
											given_prices_per_component?.find(
												(comp) => comp.id === "cabs"
											)?.price || 0

										const activitiesPrice =
											given_prices_per_component?.find(
												(comp) => comp.id === "activities"
											)?.price || 0

										if (given_currency) {
											const cabsMoney = moneyParseByDecimal(
												Number(cabsPrice || 0),
												given_currency
											)
											const activitiesMoney = moneyParseByDecimal(
												Number(activitiesPrice || 0),
												given_currency
											)
											const totalPrice = addMoney(
												addMoney(cabsMoney, activitiesMoney),
												transportExtrasMoney
											)

											return formatMoneyByIntl(totalPrice, {
												showCurrency: true,
												roundOff: true,
											})
										}
									}

									// Fallback to original calculation for backward compatibility, Incase the quote is created with old pricing (overall, per person) strategies.
									return formatMoneyByIntl(
										convertMoneyToPackageCurrency(
											addMoneyFromDifferentCurrencies(
												(
													quote.cabs as Array<{
														given_price?: number
														currency: string
													}>
												)
													.concat(
														quote.travel_activities.flatMap(
															(a) => a.ticket_tourist_configurations
														)
													)
													.concat(quote.transport_extras)
													.map(({ given_price, currency }) =>
														moneyParseByDecimal(given_price || 0, currency)
													)
											)
										),
										{
											roundOff: true,
											showCurrency: true,
											showSolidus: true,
										}
									)
								})()}*`
							: ``
					}`
		},
	}
}

function getContentForOtherExtras(
	quote: IQuote,
	config: {
		includePrice: boolean
		showServiceWise?: boolean
		convertMoneyToPackageCurrency: (money: TMoney | Array<TMoney>) => TMoney
		given_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		given_currency?: string
		priceBreakupStrategy: TPriceBreakupStrategy
		tripQuote: ITripQuote
		groupedAllocations: TGroupedAllocation[]
	}
) {
	const { other_extras } = quote
	const {
		includePrice,
		showServiceWise = true,
		convertMoneyToPackageCurrency,
		priceBreakupStrategy,
		given_currency,
	} = config
	return {
		get whatsApp() {
			return !other_extras?.length
				? ``
				: `
🪝  *_Other Special Inclusions_*
-------
${other_extras
	.map(
		(ex) =>
			`${
				ex.date
					? `*${withOrdinalSuffix(
							getDiff(
								utcTimestampToLocalDate(ex.date),
								utcTimestampToLocalDate(quote.start_date),
								"days"
							) + 1
						)} Day* - `
					: ``
			}*${ex.service.name}*${
				includePrice && showServiceWise && ex.given_price
					? `\n• *Price: ${formatMoneyByIntl(
							convertMoneyToPackageCurrency(
								moneyParseByDecimal(ex.given_price, ex.currency)
							),
							{ showCurrency: true, roundOff: true, showSolidus: true }
						)}*`
					: ``
			}`
	)
	.join("\n")}${
						includePrice &&
						["components_per_person"].includes(priceBreakupStrategy)
							? `${getContentPerPersonOptionWise({ given_currency, config, show: "extras", groupedAllocations: config?.groupedAllocations })}`
							: ""
					}${
						// The finalized given price per component extras component total contains both transport extras and other extras.
						// This section represents only Other Extras. Therefore, ignore the combined finalized
						// given price per component extras total which can be created using advance per component pricing stratgies and
						// compute only from other_extras (quote.other_extras). Also, if there are no other_extras,
						// skip showing any total here (even if a combined given price per component extras total exists).
						includePrice
							? `
*Total Other Extras Price: ${(() => {
									return formatMoneyByIntl(
										convertMoneyToPackageCurrency(
											addMoneyFromDifferentCurrencies(
												other_extras.map(({ given_price, currency }) =>
													moneyParseByDecimal(given_price || 0, currency)
												)
											)
										),
										{
											roundOff: true,
											showCurrency: true,
											showSolidus: true,
										}
									)
								})()}*`
							: ""
					}`
		},
	}
}

function getContentForIncExc(itinerary: IItinerary) {
	const { inclusion_exclusions } = itinerary

	const inclusions = inclusion_exclusions
		?.filter((inc) => inc.inclusion)
		.map(
			(inc) =>
				`+ ${
					inc.category
						? `${inc.category} ${inc.is_boolean ? "" : ` : ${inc.inclusion}`}`
						: `${inc.inclusion}`
				}${inc.comments ? `\n_${inc.comments}_` : ``}`
		)
		.join("\n")

	const exclusions = inclusion_exclusions
		?.filter((inc) => inc.exclusion)
		.map(
			(inc) =>
				`- ${
					inc.category
						? `${inc.category} ${inc.is_boolean ? "" : ` : ${inc.exclusion}`}`
						: `${inc.exclusion}`
				}${inc.comments ? `\n_${inc.comments}_` : ``}`
		)
		.join("\n")

	return {
		get whatsApp() {
			if (!inclusion_exclusions?.length) return ``

			return `${inclusions ? `*_Inclusions_*\n-----------\n${inclusions}\n\n` : ``}${
				exclusions ? `*_Exclusions_*\n-----------\n${exclusions}\n\n` : ``
			}_*NOTE*: Anything not mentioned in the inclusions is excluded_`
		},
	}
}

function getContentForDaywiseItinerary(
	itinerary: IItinerary,
	start_date: string,
	start_date_local: string | undefined,
	templateSettings: TTripQuoteEmailPdfWATemplate["daywise_schedule_section_settings"]
) {
	const hideTravelDistance = Boolean(templateSettings?.hide_travel_distance)
	const title = templateSettings?.title
	const { daywise_schedules } = itinerary
	return {
		get whatsApp() {
			let previousDay: number | undefined = undefined
			return !daywise_schedules.length
				? ``
				: `${
						title
							? `
🗓️   *_${title}_*`
							: ``
					}
${daywise_schedules
	.map((schedule) => {
		const day =
			getDiff(
				localOrUtcTimestampToLocalDate(
					schedule.start_date_local,
					schedule.start_date
				),
				localOrUtcTimestampToLocalDate(start_date_local, start_date),
				"days"
			) + 1
		const text = `${
			day !== previousDay
				? `----------\n*${withOrdinalSuffix(day)} Day - ${formatDate(
						localOrUtcTimestampToLocalDate(
							schedule.start_date_local,
							schedule.start_date
						),
						"dddd Do MMMM, YYYY"
					)}*\n----\n*${schedule.title}*`
				: `+ *${schedule.title}*`
		}${schedule.description ? `\n${schedule.description}` : ""}${
			(schedule.distance && !hideTravelDistance) || schedule.duration_formatted
				? `\n_- ` +
					[
						schedule.distance && !hideTravelDistance
							? `Approx. Distance: ${schedule.distance} km`
							: ``,
						schedule.duration_formatted
							? `Est. Travel Time: ${schedule.duration_formatted}`
							: null,
					]
						.filter(Boolean)
						.join(" • ") +
					`_`
				: ``
		}
`
		previousDay = day
		return text
	})
	.join("\n")}`
		},
	}
}

function getContentForFlights(
	quote: IQuote,
	config: {
		includePrice: boolean
		showServiceWise?: boolean
		convertMoneyToPackageCurrency: (money: TMoney | Array<TMoney>) => TMoney
		given_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		given_prices_per_person?: Array<TPricePerPersonAllocation>
		given_currency?: string
		pdfLink?: boolean
		priceBreakupStrategy: TPriceBreakupStrategy
		tripQuote: ITripQuote
	}
) {
	const { flights, flight_type } = quote

	const {
		includePrice,
		showServiceWise = false,
		convertMoneyToPackageCurrency,
		given_prices_per_component,
		given_prices_per_person,
		given_currency,
		pdfLink = false,
	} = config

	return {
		get whatsApp() {
			return !flights?.length
				? ``
				: `✈️  *_Flights_*
-----------
Flight Type: ${FLIGHT_TYPE[flight_type]} \n
${flights
	.map((flight, flightIndex) => {
		const flightSchedules = `${
			flights.length > 1 ? `*Journey ${flightIndex + 1}*` : ""
		}${flight.groups
			.map((group, index) => {
				const flightScheduleType =
					group.type === "ROUND_TRIP_ONWARD"
						? `Onward`
						: group.type === "ROUND_TRIP_RETURN"
							? `Return`
							: ""
				const needsLeadingNewline =
					flightScheduleType && (flights.length > 1 || index > 0)
				const typeLabel = flightScheduleType
					? needsLeadingNewline
						? `\n*${flightScheduleType}*`
						: `*${flightScheduleType}*`
					: ""
				return `${typeLabel}${group.schedules
					.map((schedule, index) => {
						const isList = `
*_${schedule.source.city?.name ? schedule.source.city.name + (schedule.source.iata ? ` (${schedule.source.iata})` : "") : schedule.source.iata || schedule.source.name} → ${schedule.destination.city?.name ? schedule.destination.city.name + (schedule.destination.iata ? ` (${schedule.destination.iata})` : "") : schedule.destination.iata || schedule.destination.name}_* (${humanizeDuration(getDiff(schedule.arrives_at, schedule.departs_at))})
${schedule.airline.name} ${schedule.number_plate_formatted}
*Dep:* ${formatDate(
							localOrUtcTimestampToLocalDate(
								schedule.departs_at_local,
								schedule.departs_at
							),
							"D MMM, YYYY [at] HH:mm [hrs]"
						)}
*Arr:* ${formatDate(
							localOrUtcTimestampToLocalDate(
								schedule.arrives_at_local,
								schedule.arrives_at
							),
							"D MMM, YYYY [at] HH:mm [hrs]"
						)}`
						const layover = group.schedules[index + 1]?.departs_at
							? `\n*_${humanizeDuration(
									getDiff(
										group.schedules[index].arrives_at,
										group.schedules[index + 1].departs_at
									)
								)} Layover in ${schedule.destination.city?.name || schedule.destination.iata}_*
						`
							: null
						return [isList, layover].filter(Boolean).join("\n")
					})
					.join("")}`
			})
			.join("\n")}`

		const pax = [
			flight.no_of_adults
				? pluralize("Adult", flight.no_of_adults, true)
				: null,
			flight.children ? pluralize("Child", flight.children, true) : null,
			flight.infants ? pluralize("Infant", flight.infants, true) : null,
		]
			.filter(Boolean)
			.join(" • ")

		const price =
			includePrice && showServiceWise && flight.given_price
				? `*Price: ${formatMoneyByIntl(convertMoneyToPackageCurrency(moneyParseByDecimal(flight.given_price || 0, flight.currency)), { showCurrency: true, roundOff: true, showSolidus: true })}*`
				: null

		const perPersonBreakup =
			includePrice && showServiceWise && given_currency
				? (given_prices_per_person || [])
						.filter(
							(entry) =>
								entry.service === "flight" &&
								entry.id.split("_")[0] === String(flightIndex)
						)
						.map(
							(entry) =>
								`• ${formatMoneyByIntl(
									moneyParseByDecimal(Number(entry.price), given_currency),
									{ roundOff: true }
								)} /- Per Person x ${entry.total_pax_label}`
						)
						.join("\n") || null
				: null

		return [flightSchedules, pax, price, perPersonBreakup]
			.filter(Boolean)
			.join("\n")
	})
	.join("\n\n")}${
						includePrice
							? `\n\n*Total Flights Price: ${(() => {
									// Try to get prices from given prices per components, Incase the quote is created with advance pricing strategies
									const flightsPrice =
										given_prices_per_component?.find(
											(comp) => comp.id === "flights"
										)?.price || 0

									if (
										flightsPrice &&
										given_currency &&
										Number(flightsPrice || 0) > 0
									) {
										return formatMoneyByIntl(
											moneyParseByDecimal(flightsPrice, given_currency),
											{
												showCurrency: true,
												roundOff: true,
												showSolidus: true,
											}
										)
									}

									// Fallback to original calculation for backward compatibility, Incase the quote is created with old pricing (overall, per person) strategies.
									return formatMoneyByIntl(
										convertMoneyToPackageCurrency(
											addMoneyFromDifferentCurrencies(
												(
													flights as Array<{
														currency: string
														given_price?: number
													}>
												).map(({ currency, given_price }) =>
													moneyParseByDecimal(given_price || 0, currency)
												)
											)
										),
										{
											showCurrency: true,
											roundOff: true,
											showSolidus: true,
										}
									)
								})()}*\n`
							: ``
					}
${pdfLink ? `*For more info on flights, please check PDF.*` : ""}
`
		},
	}
}

function getContentForVisas(
	quote: IQuote,
	config: {
		includePrice: boolean
		showServiceWise?: boolean
		convertMoneyToPackageCurrency: (money: TMoney | Array<TMoney>) => TMoney
		given_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		given_currency?: string
		includeItinerary?: boolean
		hideDocuments?: boolean
		priceBreakupStrategy: TPriceBreakupStrategy
		tripQuote: ITripQuote
		groupedAllocations: TGroupedAllocation[]
	}
) {
	const { visas } = quote
	const totalComponentPrice = visas.map((v) => v.given_price_money)
	const {
		includePrice,
		showServiceWise = false,
		convertMoneyToPackageCurrency,
		priceBreakupStrategy,
		given_currency,
	} = config

	const componentWisePrice = includePrice
		? `*Visa Total Price : ${formatMoneyByIntl(
				roundMoney(convertMoneyToPackageCurrency(totalComponentPrice), "ceil"),
				{ showCurrency: true, showSolidus: true }
			)}*`
		: ""

	return {
		get whatsApp() {
			if (!visas || visas.length === 0) return ``
			else
				return `🛂 *Visa*
-----------
${visas
	.map((visa) => {
		const {
			visa_type,
			given_price_money: servicePrice,
			quantity: paxCount,
			country,
		} = visa

		const details: string[] = []

		if (visa_type?.purpose) {
			details.push(`${getPurposeOfVisaDisplayName(visa_type.purpose)} Visa`)
		}
		if (visa_type?.duration_formatted) {
			details.push(`${visa_type.duration_formatted} Validity`)
		}
		if (visa_type?.entry_type) {
			details.push(`${getEntryTypeDisplayName(visa_type.entry_type)} Entry`)
		}
		if (visa_type?.processing_time_in_hours) {
			details.push(
				`Processed in ${visaProcessingTimeStr(visa_type?.processing_time_in_hours)}`
			)
		}
		if (visa_type?.apply_before_days) {
			details.push(`Apply Minimum ${visa_type.apply_before_days} Days Prior`)
		}

		const detailsList = details.map((item) => `  · ${item}`).join("\n")
		const description = visa_type?.description
			? `\n${visa_type?.description}`
			: ""

		const pax = `👤 ${paxCount} PAX`

		const itemWisePrice = showServiceWise
			? `
*- Price : ${formatMoneyByIntl(roundMoney(convertMoneyToPackageCurrency(servicePrice), "ceil"), { showCurrency: true, showSolidus: true })}*\n`
			: ""

		const documentsCheckList = !config.hideDocuments
			? [
					`\n*Documents Required:*\n`,
					"\n",
					getDocumentCheckList(visa)
						.map((doc, idx) => `${idx + 1}. ${doc}`)
						.join("\n"),
					"",
				].join("")
			: ""

		return `*🌐 ${country.name} | ✈️ ${visa_type?.name} | ${pax}*
${detailsList}
${documentsCheckList}
${description}
${itemWisePrice}`
	})
	.join("\n")}${
					includePrice &&
					["components_per_person"].includes(priceBreakupStrategy)
						? (() => {
								const content = getContentForPerPerson({
									given_currency,
									config: { ...config, quote },
									show: "visas",
								})
								return content ? `${content}\n` : ""
							})()
						: ""
				}
${componentWisePrice}`
		},
	}
}

function getDocumentCheckList(visa: IQuote["visas"][number]) {
	const { visa_service, visa_type } = visa
	const docsArr = [
		...(visa_service?.document_groups?.flatMap(
			(dg) => dg.document_types ?? []
		) ?? []),
		...(visa_type?.document_groups?.flatMap((dg) => dg?.document_types ?? []) ??
			[]),
	]
	const seenDocIds = new Set()
	const documentList = docsArr
		.filter((doc) => {
			if (!doc?.id) return false
			if (seenDocIds.has(doc.id)) return false
			seenDocIds.add(doc.id)
			return true
		})
		.map((doc) => doc.name)
	return documentList
}

function PartnerSharing({ quote: tripQuote }: { quote: ITripQuote }) {
	const { trip } = tripQuote
	const xhr = useXHR()
	const tripQuoteId = tripQuote.id
	const fetchContent = useCallback(
		async (params: object) => {
			return xhr
				.get<{
					content: string
					options: TEmailInclusionOptions
				}>(`/trip-quotes/${tripQuoteId}/as-email`, {
					params: {
						...params,
						partner_sharing: 1,
					},
				})
				.then((resp) => resp.data)
		},
		[xhr, tripQuoteId]
	)
	return (
		<EmailContent
			paddingY="4"
			copyTrackId="quote_partner"
			contentCacheKey={`/trip-quotes/${tripQuote.id}/as-email/partner`}
			fetchContent={fetchContent}
			inclusionOptionsCacheKey={`quote_partner_sharing-source_${trip?.trip_source?.id}`}
		/>
	)
}

export type TPriceBreakupStrategy =
	| "component_wise"
	| "service_wise"
	| "only_hotels_wise"
	| "only_hotel_per_person"
	| "components_per_person"

export type TWhatsappInclusionOptions = {
	incExc: boolean
	itinerary: boolean
	pdfLink: boolean
	wordDocLink: boolean
	component_prices_breakup: boolean
	price_breakup_strategy: TPriceBreakupStrategy
	hide_total_price: boolean
	tnc: boolean
	similar_word_in_hotel?: boolean
	hotel_notes?: boolean
	hide_visa_documents?: boolean
}

//@dev-notes:
// Hook for showing similar word option for hotel section for the whatsapp share options.
// The hook manages a user-togglable option, but only if globally enabled. It persists the user's choice in local storage, and keeps it in sync with the global setting.

function useSimilarWordInHotelOption({
	globalEnabled,
	baseKey,
}: {
	globalEnabled: boolean | undefined
	baseKey: string
}) {
	const optionKey = `${baseKey}_similar_word_in_hotel`

	const [optionState, setOptionState] = useSynedStorageState<
		boolean | undefined
	>(
		optionKey,
		undefined,
		(v) => (v === undefined ? "" : v ? "1" : "0"),
		(v, def) => (v === "1" ? true : v === "0" ? false : def)
	)

	// Only showing the option if global is enabled
	const show = globalEnabled !== undefined ? globalEnabled : false
	// Value: use local if set, else global default (true if enabled, undefined if not)
	const value = show
		? optionState !== undefined
			? optionState
			: true
		: undefined

	return {
		show,
		value,
		set: setOptionState,
		optionKey,
	}
}

const WhatsappSharePricingBreakupStrategiesOptions = [
	{
		title: "Price Breakup (Item Wise Detailed)",
		value: "service_wise",
	},
	{
		title: "Price Breakup (Component Wise Total)",
		value: "component_wise",
	},
	{
		title: "Price Breakup (Components Wise Per Person)",
		value: "components_per_person",
	},
	{
		title: "Price Breakup (Room Wise + Rest as Total)",
		value: "only_hotels_wise",
	},
	{
		title: "Price Breakup (Hotel Per Person + Rest as Total)",
		value: "only_hotel_per_person",
	},
]

// The `wa.me` flow appends a link to the message, so the text points at it.
// The WhatsApp send carries the file itself and drops the sentence: the
// document is attached whether or not the operator leaves it switched on.
const LINKED_DOCUMENT_SENTENCE = `For full quote details, please checkout the document linked below.`

/**
 * Send the quote straight to the customer on WhatsApp, rather than handing the
 * text to `wa.me`. Reuses the trip's send dialog so there is one send path,
 * one set of template/parameter behaviours, and the message is recorded
 * against the trip.
 */
function SendQuoteOnWhatsAppAction({
	fallback = null,
	tripQuote,
	message,
	attachment,
	disabled,
}: {
	tripQuote: Required<ITripQuote, "trip" | "itinerary">
	message: string
	attachment?: TSendTripWhatsAppAttachment
	disabled?: boolean
	// Rendered instead when this tenant has no WhatsApp integration — the
	// wa.me hand-off, so the preview is never left without a send action.
	fallback?: React.ReactNode
}) {
	const tripId = tripQuote.trip.id
	const canSendOnWhatsApp = useCanSendOnWhatsApp()
	const { overview, error, refresh } = useTripWhatsApp(
		canSendOnWhatsApp ? tripId : null
	)
	const [isDialogOpen, openDialog, closeDialog] = useDialog()

	if (!canSendOnWhatsApp || error || !overview) {
		return <>{fallback}</>
	}

	// Same shape of gate as the email side, which hides its send button when
	// the tenant has no outbound mailbox.
	const missingSender = !overview.phone_numbers.length
	const missingRecipient = !overview.contact.phone_number_plain
	const blockedReason = missingSender
		? "No WhatsApp phone number is connected."
		: missingRecipient
			? "This trip has no customer phone number."
			: undefined

	return (
		<>
			<Button
				level="primary"
				status="success"
				disabled={disabled || Boolean(blockedReason)}
				title={blockedReason}
				onClick={openDialog}
			>
				<Icons.WhatsApp /> Send via WhatsApp
			</Button>
			<SendTripWhatsAppDialog
				open={isDialogOpen}
				onClose={closeDialog}
				tripId={tripId}
				overview={overview}
				title="Send Quote on WhatsApp"
				description="Edit the message before sending. A template is optional; when chosen it is sent first, then the document, then your message."
				initialCustomMessage={message}
				attachment={attachment}
				parameterOverrides={{
					quote_pdf_url: tripQuote.itinerary.file_url || "",
					quote_pdf_filename: `Trip ${tripQuote.trip.branded_id} Quote.pdf`,
				}}
				onSuccess={async () => {
					await refresh()
					closeDialog()
				}}
			/>
		</>
	)
}

export function WhatsappContent({
	quote: tripQuote,
	inclusionOptionsCacheKey,
	defaultInclusionOptions,
	isValidating,
	tripQuotePdfTemplateSettings,
}: Readonly<{
	quote: Required<ITripQuote, "quote" | "given_quote" | "itinerary" | "trip">
	inclusionOptionsCacheKey?: string
	defaultInclusionOptions?: Partial<TWhatsappInclusionOptions>
	isValidating?: boolean
	tripQuotePdfTemplateSettings?: TTripQuotePdfTemplate
}>) {
	const {
		quote,
		given_quote,
		itinerary,
		trip,
		created_after_conversion,
		pricing_strategy,
	} = tripQuote
	const { user } = useAuthUser()
	const canSharePDF = useHasFeatureFlag("share_quote_pdf")
	const canShareWordDoc = useHasFeatureFlag("share_quote_word_doc")
	const canViewVisa =
		useHasFeatureFlag("visa") &&
		Array.isArray(quote.visas) &&
		quote.visas.length > 0
	const hotelsContainNotes = useMemo(() => {
		return quote.hotels?.some((hotel) => hotel.notes?.length)
	}, [quote])

	const baseKey =
		inclusionOptionsCacheKey || `whatsapp_content_${trip.trip_source.id}`

	const globalSimilarWord = tripQuotePdfTemplateSettings?.hotel_section_settings
		?.similar_word_in_hotel
		? isTruthy(
				tripQuotePdfTemplateSettings?.hotel_section_settings
					?.similar_word_in_hotel
			)
		: undefined

	/*
	@dev-note: Using hook for special options like (similar_word_in_hotel) which is managed by the hook on the basis of global setting in the local storage.
	*/
	const similarWordOption = useSimilarWordInHotelOption({
		globalEnabled: globalSimilarWord,
		baseKey,
	})

	const {
		file_url,
		word_doc_file_url,
		daywise_schedules,
		inclusion_exclusions,
		terms_and_conditions,
		is_generating,
	} = itinerary

	const initialState: TWhatsappInclusionOptions = useMemo(() => {
		return {
			incExc:
				!canSharePDF &&
				!canShareWordDoc &&
				Boolean(inclusion_exclusions?.length),
			itinerary: !canSharePDF && !canShareWordDoc,
			pdfLink: false,
			wordDocLink: canSharePDF ? false : canShareWordDoc,
			component_prices_breakup: false,
			price_breakup_strategy: "component_wise" as TPriceBreakupStrategy,
			hide_total_price: false,
			tnc: false,
			hotel_notes: false,
			hide_visa_documents: false,
			...defaultInclusionOptions,
		}
	}, [
		canSharePDF,
		canShareWordDoc,
		inclusion_exclusions,
		defaultInclusionOptions,
	])

	const [state, setState] = useSynedStorageState<TWhatsappInclusionOptions>(
		baseKey,
		initialState
	)

	const allOptions: Array<ITripQuote> = useMemo(
		() =>
			tripQuote.can_share_options
				? [tripQuote as ITripQuote].concat(tripQuote.options || [])
				: [tripQuote],
		[tripQuote]
	)

	const allocationsByOption = useMemo(
		() =>
			allOptions.reduce(
				(acc, option) => {
					const isPerPersonStrategy =
						option.pricing_strategy === "per_person_v1" ||
						option.pricing_strategy === "per_person_v2" ||
						option.pricing_strategy === "per_component_per_person_v2"

					const costPriceComponents = option?.per_person_cost_price_components

					const isChildrenInCabsEnabled = isPerPersonStrategy
						? Boolean(
								costPriceComponents?.include_children_in_cabs_start_age &&
								Number(
									costPriceComponents?.include_children_in_cabs_start_age
								) > 0
							)
						: false

					const includeChildrenInCabsStartAge = isChildrenInCabsEnabled
						? Number(costPriceComponents?.include_children_in_cabs_start_age)
						: false

					// here in case if straregy is not per person then we will include adults with extra bed in cabs by default because in case of overall strategy we don't have any option to exclude adults with extra bed in cabs.
					const includeAdultsWithExtraBedInCabs = isPerPersonStrategy
						? costPriceComponents?.include_adults_with_extra_bed_in_cabs !==
							undefined
							? isTruthy(
									costPriceComponents.include_adults_with_extra_bed_in_cabs
								)
							: false
						: true

					const includeChildrenInExtras = isPerPersonStrategy
						? isTruthy(costPriceComponents?.include_children_in_extras)
						: true

					const excludeAdultsCountInCabs = isPerPersonStrategy
						? (costPriceComponents?.exclude_adults_count_in_cabs ?? 0) > 0
							? (costPriceComponents?.exclude_adults_count_in_cabs ?? 0)
							: 0
						: 0

					const { allocation, warnings } = allocatePax({
						quote: option.quote,
						costingCurrency: tripQuote.currency ?? "",
						excludeAdultsCountInCabs,
						includeChildrenInCabsStartAge,
						includeChildrenInExtras,
						includeAdultsWithExtraBedInCabs,
					})

					acc[option.id] = {
						allocation,
						warnings,
					}

					return acc
				},
				{} as Record<
					string | number,
					{
						allocation: ReturnType<typeof allocatePax>["allocation"]
						warnings: Array<string>
					}
				>
			),
		[allOptions, tripQuote.currency]
	)

	const groupedAllocations = useMemo(
		() => groupAllocationsByConfiguration(allocationsByOption, allOptions),
		[allocationsByOption, allOptions]
	)

	const totalPriceUsingAllocationByOption = useMemo(
		() =>
			Object.entries(allocationsByOption).reduce(
				(acc, [optionId, { allocation }]) => {
					acc[optionId] = (allocation || []).reduce<number>(
						(total, { price, pax_count }) =>
							total +
							Number(formatMoneyByDecimal(multipleMoneyBy(price, pax_count))),
						0
					)

					return acc
				},
				{} as Record<string, number>
			),
		[allocationsByOption]
	)
	const totalCostPricewithoutFlightsByOption = useMemo(
		() =>
			Object.fromEntries(
				allOptions.map((option) => {
					const totalCostPrice = option.cost_prices_per_component
						?.filter(({ id }) => id !== "flights")
						.reduce(
							(total, { price }) =>
								addMoney(total, {
									amount: price,
									currency: given_quote.given_currency,
								}),
							{
								amount: 0,
								currency: given_quote.given_currency,
							}
						)

					return [option.id, totalCostPrice]
				})
			),
		[allOptions, given_quote.given_currency]
	)

	// price mismatch used to check whether the total cost price without flights is greater than the total allocation price or not. If it is greater then we will hide the options "hotel per person + rest as total" and "component wise per person" from the whatsapp share pricing breakup strategies options.
	const priceMismatch = useMemo(
		() =>
			Object.entries(allocationsByOption).some(([optionId, { allocation }]) => {
				if (!allocation) {
					return false
				}

				const allocationPrice = Number(
					formatMoneyByDecimal(
						moneyParseByDecimal(
							totalPriceUsingAllocationByOption[optionId],
							given_quote.given_currency
						)
					)
				)

				const costPrice = Number(
					formatMoneyByDecimal(
						moneyParseByDecimal(
							totalCostPricewithoutFlightsByOption[optionId]?.amount ?? 0,
							given_quote.given_currency
						)
					)
				)

				return costPrice > allocationPrice
			}),
		[
			allocationsByOption,
			totalPriceUsingAllocationByOption,
			totalCostPricewithoutFlightsByOption,
			given_quote.given_currency,
		]
	)
	// filtering the whatsapp share pricing breakup strategies options based on the price mismatch and warnings.
	const filteredSharePricingBreakupStrategiesOptions = useMemo(() => {
		if (
			priceMismatch ||
			Object.values(allocationsByOption).some(
				({ warnings }) => warnings.length > 0
			)
		) {
			return WhatsappSharePricingBreakupStrategiesOptions.filter(
				(option) =>
					!["only_hotel_per_person", "components_per_person"].includes(
						option.value
					)
			)
		}

		return WhatsappSharePricingBreakupStrategiesOptions
	}, [priceMismatch, allocationsByOption])

	const processingPaymentLinkTransactionForOption = allOptions.find(
		(o) => o.given_quote?.pg_invoice?.unverified_successful_transactions_count
	)

	const { used_for_conversion } = given_quote

	const { start_date, start_date_local } = quote

	const isV1PricingStrategy =
		pricing_strategy === "overall_v1" || pricing_strategy === "per_person_v1"

	const convertMoneyToPackageCurrencyForFlights =
		makePackageCurrencyConverterForTripQuote(tripQuote, "flights")

	const recipient = getRecipientName(trip)
	const greetings = () => {
		return `Hi ${recipient},

Greetings from ${trip.tenant_brand?.short_name || user?.tenant?.short_name}.

${
	created_after_conversion
		? `As per our discussion, following is the *updated quote after conversion* details.`
		: used_for_conversion
			? `Thank you for allowing us the opportunity to serve you. Following are the *confirmed quote* details for your reference.`
			: `Thank you for your query with us. As per your requirements, following are the package details.`
}
${
	created_after_conversion
		? `
*After Conversion Updated Quote*
`
		: used_for_conversion
			? `
✅  *Confirmed Quote*
`
			: ``
}`
	}

	const nightDayString =
		quote.nights > 0
			? `${pluralize("Night", quote.nights, true)}, ${pluralize("Day", quote.days, true)}`
			: `${pluralize("Day", quote.days, true)}`

	const tripDetails = `*Trip ID ${trip.branded_id}*${
		allOptions.length > 1
			? ` _(${pluralize("Package Category/Option", allOptions.length, true)})_`
			: ``
	}
----------
*${trip.destinations.map((d) => d.name).join(", ")} Trip*
• *${formatDate(
		localOrUtcTimestampToLocalDate(quote.start_date_local, quote.start_date),
		"DD MMM, YYYY"
	)}* _for_ *${nightDayString}*
• *${quote.pax_string}*${
		trip.reference_id
			? `
• *Ref ID: ${trip.reference_id}*`
			: ``
	}`

	const packagePrice = (tripQuote: ITripQuote) => {
		const { given_quote, quote } = tripQuote
		const { complimentary_count, no_of_adults, no_of_children } = quote
		if (!given_quote) return ``
		const { given_price, given_prices_per_person, gst_included, pg_invoice } =
			given_quote
		const chargeAblePax = no_of_adults + no_of_children - complimentary_count
		const perPersonPrice = roundMoney(
			divideMoneyBy(
				moneyParseByDecimal(given_price, given_quote.given_currency),
				chargeAblePax,
				"ceil"
			),
			"ceil"
		)
		const perPersonPriceFormatted = formatMoneyByIntl(perPersonPrice, {
			showSolidus: true,
		})

		const groupedPerPersonPrices = groupPricesPerPersonByService(
			given_quote?.given_prices_per_person ?? []
		)
		return `${
			given_prices_per_person?.length
				? `
*Price (${given_quote.given_currency}):*
${groupedPerPersonPrices
	.map(
		({ service, prices, title, subtotal }) =>
			`${title ? `*${title}*\n` : ""}${prices
				.map(
					({ per_pax_label, total_pax_label, price }) =>
						`• *${formatMoneyByIntl(
							moneyParseByDecimal(Number(price), given_quote.given_currency),
							{ roundOff: true }
						)} /- ${
							service === "accommodation" ? "Per" : ""
						} ${per_pax_label}* x ${total_pax_label}`
				)
				.join("\n")} ${
				groupedPerPersonPrices.length > 1
					? `\n*Subtotal: ${formatMoneyByIntl(
							moneyParseByDecimal(
								Number(subtotal.amount),
								given_quote.given_currency
							),
							{ roundOff: true, showSolidus: true }
						)}*`
					: ``
			}`
	)
	.join("\n\n")}`
				: ``
		}${complimentary_count > 0 ? `*${perPersonPriceFormatted} Per Person X ${chargeAblePax} Pax + ${complimentary_count} FOC*` : ""}\n
${
	!state.hide_total_price
		? `*Total${
				!given_prices_per_person?.length
					? ` Price (${given_quote.given_currency})`
					: ``
			}: ${formatMoneyByIntl(
				moneyParseByDecimal(Number(given_price), given_quote.given_currency),
				{
					roundOff: true,
					showSolidus: true,
				}
			)}*${given_quote.tax_label ? ` _(${gst_included ? "inc." : "exc."} ${given_quote.tax_label})_` : ""}`
		: `${given_prices_per_person?.length ? (given_quote.tax_label ? ` _(${gst_included ? "inc." : "exc."} ${given_quote.tax_label})_` : "") : ""}`
}${given_quote.customer_remarks ? `\n\n*_Notes_*\n-------\n${given_quote.customer_remarks}\n-------\n` : ""}${
			pg_invoice && !processingPaymentLinkTransactionForOption
				? `

👉 *Book Now:* ${pg_invoice.link}\nPay Just ${formatMoneyByIntl(pg_invoice.original_amount, { showCurrency: true })} as First Instalment\n_Multiple Payments Options (UPI, DC, CC, NB etc.)_`
				: ``
		}`
	}

	const whatsappContent = `${([] as Array<string>)
		.concat([
			greetings(),
			tripDetails,
			...allOptions.map((tripQuote, index, arr) => {
				const { quote, trip } = tripQuote
				return [
					arr.length > 1 ? `⏬ *OPTION ${index + 1}: ${tripQuote.name}*` : ``,
					packagePrice(tripQuote),
					getContentForHotels(quote, {
						...getPriceDisplayConfig(
							"hotels",
							state.price_breakup_strategy,
							state.component_prices_breakup,
							state.pdfLink
						),
						convertMoneyToPackageCurrency:
							makePackageCurrencyConverterForTripQuote(tripQuote, "hotels"),
						similarWordInHotel:
							state.similar_word_in_hotel && similarWordOption.value,
						given_prices_per_component:
							tripQuote.given_quote?.given_prices_per_component,
						given_currency: tripQuote.given_quote?.given_currency,
						includeHotelNotes: state.hotel_notes && hotelsContainNotes,
						destinations: trip?.destinations,
						priceBreakupStrategy: state.price_breakup_strategy,
						tripQuote,
						allocation: allocationsByOption[tripQuote.id]?.allocation,
					}).whatsApp,
				]
					.map((t) => t.trim())
					.filter(Boolean)
					.join("\n\n")
			}),
			allOptions.length > 1
				? "-------\n⏩ *For All Options*\nDetails below are applicable for all the options.\n-------"
				: "",
			getContentForCabs(
				quote,
				{
					...getPriceDisplayConfig(
						"cabs",
						state.price_breakup_strategy,
						state.component_prices_breakup,
						state.pdfLink
					),
					convertMoneyToPackageCurrency:
						makePackageCurrencyConverterForTripQuote(tripQuote),
					given_prices_per_component: given_quote.given_prices_per_component,
					given_currency: given_quote.given_currency,
					tripQuote: tripQuote,
					priceBreakupStrategy: state.price_breakup_strategy,
					groupedAllocations,
				},
				tripQuotePdfTemplateSettings?.transport_and_activity_section_settings
			).whatsApp,
			getContentForFlights(quote, {
				...getPriceDisplayConfig(
					"flights",
					state.price_breakup_strategy,
					state.component_prices_breakup,
					state.pdfLink
				),
				convertMoneyToPackageCurrency: (money: TMoney | Array<TMoney>) => {
					return convertMoneyToPackageCurrencyForFlights(
						money,
						isV1PricingStrategy ? true : false
					)
				},
				given_prices_per_component: given_quote.given_prices_per_component,
				given_prices_per_person: given_quote.given_prices_per_person,
				given_currency: given_quote.given_currency,
				priceBreakupStrategy: state.price_breakup_strategy,
				tripQuote,
			}).whatsApp,
			getContentForVisas(quote, {
				...getPriceDisplayConfig(
					"visas",
					state.price_breakup_strategy,
					state.component_prices_breakup,
					state.pdfLink
				),
				convertMoneyToPackageCurrency: makePackageCurrencyConverterForTripQuote(
					tripQuote,
					"visas"
				),
				includeItinerary: state.itinerary,
				hideDocuments: state.hide_visa_documents,
				priceBreakupStrategy: state.price_breakup_strategy,
				tripQuote,
				given_currency: given_quote.given_currency,
				groupedAllocations,
			}).whatsApp,
			getContentForOtherExtras(quote, {
				...getPriceDisplayConfig(
					"extras",
					state.price_breakup_strategy,
					state.component_prices_breakup,
					state.pdfLink
				),
				convertMoneyToPackageCurrency: makePackageCurrencyConverterForTripQuote(
					tripQuote,
					"extras"
				),
				given_prices_per_component: given_quote.given_prices_per_component,
				given_currency: given_quote.given_currency,
				tripQuote: tripQuote,
				priceBreakupStrategy: state.price_breakup_strategy,
				groupedAllocations,
			}).whatsApp,
			state.incExc ? getContentForIncExc(itinerary).whatsApp : ``,
			state.itinerary
				? getContentForDaywiseItinerary(
						itinerary,
						start_date,
						start_date_local,
						tripQuotePdfTemplateSettings?.daywise_schedule_section_settings
					).whatsApp
				: ``,
			itinerary.other_info
				? `*${tripQuotePdfTemplateSettings?.other_info_section_settings?.title || "Important Notes"}*\n-------\n${itinerary.other_info}`
				: ``,
			state.tnc && itinerary.terms_and_conditions
				? `*${tripQuotePdfTemplateSettings?.terms_section_settings?.title || "Terms and Conditions"}*\n-------\n${itinerary.terms_and_conditions.description}`
				: ``,
			(state.pdfLink && canSharePDF) || (state.wordDocLink && canShareWordDoc)
				? LINKED_DOCUMENT_SENTENCE
				: ``,
		])
		.map((t) => t.trim())
		.filter(Boolean)
		.join("\n\n")}`

	// Offered whenever a PDF exists, independent of the link toggle above --
	// the send dialog has its own switch, so attaching does not depend on
	// having ticked something before opening it. Only the PDF can travel this
	// way; the Word document is generated inline for email and never stored.
	const whatsappAttachment: TSendTripWhatsAppAttachment | undefined =
		canSharePDF && file_url && !is_generating
			? {
					tripQuoteId: tripQuote.id,
					fileName: `Quote PDF`,
				}
			: undefined

	// The link is never appended on this path, so the sentence pointing at it
	// would be dangling.
	const whatsappSendContent = whatsappContent
		.replace(LINKED_DOCUMENT_SENTENCE, "")
		.replace(/\n{3,}/g, "\n\n")
		.trim()

	/*
@dev-note: Keeping the local saved preference state of the component in sync for the modular options like (similar_word_in_hotel) which is managed by the hook on the basis of global setting in the local storage.
*/
	useEffect(() => {
		if (
			similarWordOption.show &&
			state.similar_word_in_hotel !== similarWordOption.value
		) {
			setState({ ...state, similar_word_in_hotel: similarWordOption.value })
		}
	}, [similarWordOption.show, similarWordOption.value, state, setState])

	/*
@dev-note: Keeping the local saved preference state of the component in sync for the conditional options like (component_prices_breakup, pdf_link, word_doc), which is conditionally not allowed on different use-cases.
*/
	useEffect(() => {
		if (state.incExc && !inclusion_exclusions.length) {
			setState({
				...state,
				incExc: false,
			})
		}

		if (state.itinerary && !daywise_schedules.length) {
			setState({
				...state,
				itinerary: false,
			})
		}

		if (state.tnc && !terms_and_conditions) {
			setState({
				...state,
				tnc: false,
			})
		}

		if (
			state.component_prices_breakup &&
			!isComponentPricesBreakupAllocationPossible(
				tripQuote,
				allOptions,
				tripQuote.pricing_strategy
			)
		) {
			setState({ ...state, component_prices_breakup: false })
		}

		if (state.pdfLink && !canSharePDF) {
			setState({ ...state, pdfLink: false })
		}

		if (
			state.wordDocLink &&
			canSharePDF &&
			!word_doc_file_url &&
			!canShareWordDoc
		) {
			setState({ ...state, wordDocLink: false })
		}
	}, [
		allOptions,
		inclusion_exclusions,
		canSharePDF,
		canShareWordDoc,
		word_doc_file_url,
		daywise_schedules,
		terms_and_conditions,
		tripQuote,
		state,
		setState,
	])

	return (
		<Box position="relative">
			<Box paddingX="4" paddingY="2">
				<Text fontSize="sm">
					<Icons.LightBulb opacity="70" /> Use toggles to customize the content
					according to your needs.
				</Text>
			</Box>

			{isValidating ? (
				<Box style={{ minHeight: "500px" }}>
					<Spinner alignCenter padding="4" />
				</Box>
			) : (
				<>
					<Box
						position="sticky"
						top="0"
						padding="4"
						zIndex="10"
						bgColor="subtle"
						borderBottomWidth="1"
						boxShadow="sm"
					>
						<Box display="flex" flexWrap="wrap" gap="4">
							{inclusion_exclusions?.length ? (
								<Box as="label">
									<CheckboxInput
										checked={state.incExc}
										onChange={() =>
											setState({
												...state,
												incExc: !state.incExc,
											})
										}
									/>{" "}
									Inc/Exc
								</Box>
							) : null}
							{tripQuote.is_root &&
							isComponentPricesBreakupAllocationPossible(
								tripQuote,
								allOptions,
								tripQuote.pricing_strategy
							) ? (
								<Inline gap="1">
									<Box as="label">
										<CheckboxInput
											checked={state.component_prices_breakup}
											onChange={(e) => {
												setState({
													...state,
													component_prices_breakup:
														!state.component_prices_breakup,
													price_breakup_strategy: !e.currentTarget.checked
														? "component_wise"
														: state.price_breakup_strategy,
												})
											}}
										/>
										{""}
										{/*Prices Breakup*/}
									</Box>
									<Box position={"relative"}>
										<Box as="label" htmlFor="price_breakup_strategy_options">
											{(() => {
												const selected =
													filteredSharePricingBreakupStrategiesOptions.find(
														(o) => o.value === state.price_breakup_strategy
													)
												return (
													selected?.title ||
													"Price Breakup (Component Wise Total)"
												)
											})()}
											<Icons.ChevronDown />
										</Box>
										<Box
											position="absolute"
											overflow="hidden"
											inset="0"
											title="Select More options"
										>
											<select
												id="price_breakup_strategy_options"
												value={state.price_breakup_strategy}
												style={{
													background: "transparent",
													opacity: 0,
												}}
												onChange={(e) => {
													setState({
														...state,
														price_breakup_strategy: e.currentTarget
															.value as TPriceBreakupStrategy,
													})
												}}
											>
												{filteredSharePricingBreakupStrategiesOptions.map(
													(o, i) => (
														<option value={o.value} key={i}>
															{o.title}
														</option>
													)
												)}
											</select>
										</Box>
									</Box>
									<Icons.NewBadge />
								</Inline>
							) : null}
							<Box as="label">
								<CheckboxInput
									checked={state.hide_total_price}
									onChange={() =>
										setState({
											...state,
											hide_total_price: !state.hide_total_price,
										})
									}
								/>{" "}
								Hide Total Price
							</Box>
							{daywise_schedules?.length ? (
								<Box as="label">
									<CheckboxInput
										checked={state.itinerary}
										onChange={() =>
											setState({
												...state,
												itinerary: !state.itinerary,
											})
										}
									/>{" "}
									Itinerary
								</Box>
							) : null}
							{file_url && canSharePDF && !is_generating ? (
								<Box as="label">
									<CheckboxInput
										checked={state.pdfLink}
										onChange={() =>
											setState({
												...state,
												pdfLink: !state.pdfLink,
												wordDocLink: false,
											})
										}
									/>{" "}
									PDF
								</Box>
							) : null}
							{!canSharePDF && word_doc_file_url && canShareWordDoc ? (
								<Box as="label">
									<CheckboxInput
										checked={state.wordDocLink}
										onChange={() =>
											setState({
												...state,
												pdfLink: false,
												wordDocLink: !state.wordDocLink,
											})
										}
									/>{" "}
									Word Doc
								</Box>
							) : null}
							{terms_and_conditions ? (
								<Box as="label">
									<CheckboxInput
										checked={state.tnc}
										onChange={() =>
											setState({
												...state,
												tnc: !state.tnc,
											})
										}
									/>{" "}
									Terms
								</Box>
							) : null}
							{similarWordOption.show ? (
								<Box as="label">
									<CheckboxInput
										checked={isTruthy(state.similar_word_in_hotel)}
										onChange={() => {
											const newValue = !isTruthy(state.similar_word_in_hotel)
											setState({
												...state,
												similar_word_in_hotel: newValue,
											})
											similarWordOption.set(newValue)
										}}
									/>{" "}
									Similar Word in Hotels
								</Box>
							) : null}
							{hotelsContainNotes ? (
								<Box as="label">
									<CheckboxInput
										checked={isTruthy(state.hotel_notes)}
										onChange={() => {
											setState({
												...state,
												hotel_notes: !state.hotel_notes,
											})
										}}
									/>{" "}
									Hotel Notes
								</Box>
							) : null}
							{canViewVisa ? (
								<Box as="label">
									<CheckboxInput
										checked={isTruthy(state.hide_visa_documents)}
										onChange={() => {
											setState({
												...state,
												hide_visa_documents: !state.hide_visa_documents,
											})
										}}
									/>{" "}
									Hide Visa Documents
								</Box>
							) : null}
							{isValidating ? (
								<Box>
									<Icons.Refresh spin />
								</Box>
							) : null}
							<Inline gap="2" marginLeft="auto" alignItems="center">
								{canSharePDF ? (
									file_url && !is_generating ? (
										<Box>
											<Button
												as="a"
												target="_blank"
												rel="noopener noreferrer"
												href={file_url}
												level="primary"
												size="sm"
											>
												Download PDF
											</Button>
										</Box>
									) : (
										<Box fontSize="sm">
											<Button
												title="Generating Quote PDF File..."
												disabled
												size="sm"
											>
												<Spinner />
											</Button>
										</Box>
									)
								) : null}
							</Inline>
						</Box>
					</Box>
					<Box
						key={whatsappContent}
						roundedBottom="md"
						overflow="hidden"
						position="relative"
					>
						{state.pdfLink ? (
							<Alert
								status="warning"
								title="PDF Links NOT Recommended. Instead, download and share the PDF file."
								rounded="none"
							>
								<Stack gap="4">
									<Text>
										Links, shared to new customers are, <b>NOT clickable</b> and
										WhatsApp might <b>block the chat</b> because of recent
										policy changes. This also increases message ignorance due to
										security concerns.
									</Text>
									<Text color="default">
										<Icons.LightBulb /> Instead, download the PDF and share it
										as a separate message. This also adds Preview Thumnails and
										improves click-through rate.
									</Text>
								</Stack>
							</Alert>
						) : null}
						<ShareViaWhatsApp
							text={whatsappContent}
							url={
								state.pdfLink
									? file_url
									: state.wordDocLink
										? word_doc_file_url
										: undefined
							}
							phoneNumber={trip?.contact?.phone_numbers}
							trackingContentType="quotes"
							trackingContentId="guest"
							disabled={isValidating}
							// Deliberately not the previewed `text`: this path attaches the
							// document itself and appends no link, so it sends the copy
							// with the "see the linked document" sentence stripped.
							renderSendAction={({ defaultAction }) => (
								<SendQuoteOnWhatsAppAction
									tripQuote={tripQuote}
									message={whatsappSendContent}
									attachment={whatsappAttachment}
									disabled={isValidating}
									fallback={defaultAction}
								/>
							)}
						/>
					</Box>
				</>
			)}
		</Box>
	)
}

/**
 * Utility to determine if component_prices_breakup should be allowed for a given trip quote and its options.
 *
 * - Only allowed if the root quote (is_root) AND
 *   (there is only one option OR all options have only hotel services)
 */
function isComponentPricesBreakupAllocationPossible(
	tripQuote: ITripQuote,
	allOptions: ITripQuote["options"],
	pricingStrategy: ITripQuote["pricing_strategy"]
): boolean {
	if (!tripQuote.is_root) return false
	if ((allOptions || []).length === 1) return true
	if (
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"
	)
		return true
	return (allOptions || []).every(function hasOnlyHotelService(tripQuote) {
		const { quote } = tripQuote
		return !(
			quote.cabs?.length ||
			quote.travel_activities?.length ||
			quote.transport_extras?.length ||
			quote.flights?.length ||
			quote.other_extras?.length
		)
	})
}

const getPriceDisplayConfig = (
	component: TComponentKey,
	strategy: TPriceBreakupStrategy,
	priceBreakupEnabled: boolean,
	pdfLink: boolean
) => {
	if (!priceBreakupEnabled) {
		return { includePrice: false, showServiceWise: false }
	}

	switch (strategy) {
		case "component_wise":
			// Show component totals only (aggregated prices)
			return { includePrice: true, showServiceWise: false, pdfLink }

		case "service_wise":
			// Show individual service prices for all components
			return { includePrice: true, showServiceWise: true, pdfLink }

		case "only_hotels_wise":
			// Show service-wise only for hotels, component totals for others
			return {
				includePrice: true,
				showServiceWise: component === "hotels",
				pdfLink,
			}

		default:
			// default to component_wise
			return { includePrice: true, showServiceWise: false, pdfLink }
	}
}

/**
 * Returns the recipient name for greetings based on the trip's source.
 */
function getRecipientName(trip: ITrip): string {
	const name =
		trip.trip_source.is_agent && trip.trip_source_contact
			? trip.trip_source_contact.name
			: trip.contact?.name
	return name
}

type TAllocationPriceType = "hotels" | "cabs" | "extras" | "flights" | "visas"

/**
 * Generates the per-person price breakdown text for WhatsApp sharing.
 * Formats component prices for hotels, visas, cabs, extras, or flights.
 */
function getContentForPerPerson({
	given_currency,
	show = "hotels",
	config,
	title = "Price",
}: {
	given_currency?: string
	show?: TAllocationPriceType
	config: {
		tripQuote: ITripQuote
		allocation?: TAllocation[]
		quote?: IQuote
		convertMoneyToPackageCurrency?: (money: TMoney | Array<TMoney>) => TMoney
	}
	title?: string
}) {
	const { tripQuote, allocation = [] } = config
	const { multipliers, given_quote, pricing_strategy } = tripQuote

	// Determine the target package currency.
	const targetCurrency =
		given_currency ||
		tripQuote.given_quote?.given_currency ||
		tripQuote.currency ||
		"INR"

	// Check if this quote uses advance per-component pricing strategies.
	const isPerComponent =
		pricing_strategy === "per_component_v2" ||
		pricing_strategy === "per_component_per_person_v2"

	// Create a currency converter from the currency pairs of the quote.
	const converter = makeCurrencyConverter(
		given_quote?.currency_pairs ?? [],
		true
	)

	/**
	 * Retrieves the price multiplier for a quote component.
	 * Advanced per-component strategies use component-specific multipliers.
	 * Overall and per-person strategies use the overall quote package multiplier.
	 */
	const getComponentMultiplier = (
		componentKey: "hotels" | "cabs" | "activities" | "extras" | "visas"
	): number => {
		if (isPerComponent) {
			return getMultiplierForPerComponentPricing(tripQuote, componentKey)
		}
		const overallMultiplier = getMultiplierForOverallPerPersonPricing(tripQuote)
		if (overallMultiplier > 0) {
			return overallMultiplier
		}
		return Number(multipliers?.[componentKey] || 1)
	}

	/**
	 * Calculates the component price for a single passenger allocation.
	 * Applies component multipliers and converts the amount to the target currency.
	 */
	const getPrice = (p: TAllocation) => {
		switch (show) {
			case "hotels": {
				const hotelMultiplier = getComponentMultiplier("hotels")

				// Calculate hotel base price and hotel extras with the hotel multiplier.
				const hotelPrice = moneyParseByDecimal(p.hotels_price, p.currency)
				const hotelExtraPrice = moneyParseByDecimal(
					p.hotel_extras_price || 0,
					p.currency
				)

				const hotelPriceWithMultiplier = multipleMoneyBy(
					hotelPrice,
					hotelMultiplier
				)
				const hotelExtraPriceWithMultiplier = multipleMoneyBy(
					hotelExtraPrice,
					hotelMultiplier
				)

				return formatMoneyByDecimal(
					converter(
						targetCurrency,
						addMoney(hotelPriceWithMultiplier, hotelExtraPriceWithMultiplier)
					)
				)
			}

			case "cabs": {
				// Calculate transport, activities, and transport extras with their multipliers.
				const cabMultiplier = getComponentMultiplier("cabs")
				const activityMultiplier = getComponentMultiplier("activities")
				const transportExtraMultiplier = getComponentMultiplier("extras")

				const cabPrice = multipleMoneyBy(
					moneyParseByDecimal(p.cabs_price, p.currency),
					cabMultiplier
				)
				const activityPrice = multipleMoneyBy(
					moneyParseByDecimal(p.travel_activities_price || 0, p.currency),
					activityMultiplier
				)
				const transportExtraPrice = multipleMoneyBy(
					moneyParseByDecimal(p.transport_extras_price || 0, p.currency),
					transportExtraMultiplier
				)
				return formatMoneyByDecimal(
					converter(
						targetCurrency,
						addMoney(cabPrice, activityPrice, transportExtraPrice)
					)
				)
			}

			case "extras": {
				const extrasMultiplier = getComponentMultiplier("extras")

				// Calculate non-transport and non-hotel extras with the extras multiplier.
				const totalExtras = moneyParseByDecimal(p.extras_price, p.currency)
				const transportExtras = moneyParseByDecimal(
					p.transport_extras_price || 0,
					p.currency
				)
				const hotelExtras = moneyParseByDecimal(
					p.hotel_extras_price || 0,
					p.currency
				)
				const otherExtras = subtractMoney(
					subtractMoney(totalExtras, transportExtras),
					hotelExtras
				)

				const extrasPriceWithMultiplier = multipleMoneyBy(
					otherExtras,
					extrasMultiplier
				)

				return formatMoneyByDecimal(
					converter(targetCurrency, extrasPriceWithMultiplier)
				)
			}

			case "flights": {
				// Convert flight price directly to the target currency.
				const flightPrice = moneyParseByDecimal(p.flights_price, p.currency)

				return formatMoneyByDecimal(converter(targetCurrency, flightPrice))
			}

			case "visas": {
				const visaMultiplier = getComponentMultiplier("visas")

				// Calculate visa price with the visa multiplier.
				const visaPrice = moneyParseByDecimal(p.visas_price, p.currency)

				const visaPriceWithMultiplier = multipleMoneyBy(
					visaPrice,
					visaMultiplier
				)

				return formatMoneyByDecimal(
					converter(targetCurrency, visaPriceWithMultiplier)
				)
			}

			default:
				return null
		}
	}

	// ---------------------------------------------------------------------------
	// 1. HOTELS BREAKDOWN
	// ---------------------------------------------------------------------------
	// Shows room-sharing configurations (for example: "Single Sharing x 3 Pax").
	if (show === "hotels") {
		const allocationContent = allocation
			.filter((p) => Number(getPrice(p) || 0) > 0)
			.map((p) => {
				const price = getPrice(p)
				const scaledPrice = Number(price)

				return `• ${
					price
						? `*${formatMoneyByIntl(
								roundMoney(
									moneyParseByDecimal(Number(scaledPrice), targetCurrency),
									"ceil"
								),
								{
									roundOff: true,
									showSolidus: true,
								}
							)}*`
						: ""
				} *Per ${p.per_pax_label}* x *${p.total_pax_label}*`
			})
			.join("\n")

		const displayTitle = title
			? title.charAt(0).toUpperCase() + title.slice(1)
			: "Price"

		const displayCurrency =
			given_currency || tripQuote.given_quote?.given_currency

		return allocationContent
			? `\n*${displayTitle}${displayCurrency ? ` (${displayCurrency})` : ""}:*\n${allocationContent}`
			: ""
	}

	// ---------------------------------------------------------------------------
	// 2. VISAS BREAKDOWN
	// ---------------------------------------------------------------------------
	// Shows per-pax unit price for each visa service and passenger count.
	if (show === "visas") {
		// Read visa services from quote configuration.
		const visas =
			(config as { quote?: IQuote })?.quote?.visas ||
			tripQuote?.quote?.visas ||
			(tripQuote as unknown as { visas?: typeof tripQuote.quote.visas })
				?.visas ||
			[]

		// Determine target package currency.
		const targetCurrency =
			given_currency ||
			tripQuote.given_quote?.given_currency ||
			tripQuote.currency ||
			"INR"

		// Use the visa currency converter with markup when available.
		const convertMoney =
			(config as { convertMoneyToPackageCurrency?: (m: TMoney) => TMoney })
				?.convertMoneyToPackageCurrency ||
			makePackageCurrencyConverterForTripQuote(tripQuote, "visas")

		const visaRows = visas
			.map((visa) => {
				const paxCount =
					Number(visa.quantity || (visa as unknown as { pax?: number })?.pax) ||
					1

				if (paxCount <= 0) {
					return null
				}

				// Build money object for visa service price.
				const visaPrice: TMoney = visa.given_price_money
					? {
							amount: Number(visa.given_price_money.amount) || 0,
							currency: visa.given_price_money.currency || targetCurrency,
						}
					: moneyParseByDecimal(
							Number(visa.given_price) || 0,
							visa.currency || targetCurrency
						)

				// Convert total service price to package currency with multiplier.
				const convertedVisaPrice = convertMoney(visaPrice)

				if (!convertedVisaPrice || convertedVisaPrice.amount <= 0) {
					return null
				}

				// Divide total service price by passenger count to get unit price per pax.
				const perPaxMoney = roundMoney(
					divideMoneyBy(convertedVisaPrice, paxCount, "ceil"),
					"ceil"
				)

				// Format price with solidus (for example: "143 /-").
				const formattedPrice = formatMoneyByIntl(perPaxMoney, {
					roundOff: true,
					showSolidus: true,
				})

				const displayPrice = formattedPrice.includes("/-")
					? formattedPrice
					: `${formattedPrice} /-`

				return `• *${displayPrice}* *Per Pax* x *${paxCount} Pax*`
			})
			.filter((row): row is string => Boolean(row))

		if (visaRows.length === 0) {
			return ""
		}

		const displayTitle = title
			? title.charAt(0).toUpperCase() + title.slice(1)
			: "Price"

		const displayCurrency =
			given_currency || tripQuote.given_quote?.given_currency

		return `\n*${displayTitle}${displayCurrency ? ` (${displayCurrency})` : ""}:*\n${visaRows.join("\n")}`
	}

	// ---------------------------------------------------------------------------
	// 3. CABS, EXTRAS, AND FLIGHTS BREAKDOWN
	// ---------------------------------------------------------------------------
	// Extract all paying passenger allocations with non-zero prices.
	const payingAllocations = allocation
		.map((p) => {
			const price = getPrice(p)
			if (!price || Number(price) <= 0) {
				return null
			}

			const scaledPrice = Number(price)
			const roundedMoney = roundMoney(
				moneyParseByDecimal(scaledPrice, targetCurrency),
				"ceil"
			)
			const type: "adult" | "child" =
				p.type === "child" || p.per_pax_label?.toLowerCase().includes("child")
					? "child"
					: "adult"

			const count = p.pax_count || 1
			const ages = p.ages || []

			return {
				price,
				scaledPrice,
				roundedMoney,
				type,
				count,
				ages,
			}
		})
		.filter((item): item is NonNullable<typeof item> => Boolean(item))

	// Separate passenger allocations into adults and children.
	const adultItems = payingAllocations.filter((item) => item.type === "adult")
	const childItems = payingAllocations.filter((item) => item.type === "child")

	type TAdultGroup = {
		roundedMoney: TMoney
		count: number
	}

	// Group adult passengers by unit price.
	// Adults with the same unit price are grouped together into a single row.
	// Adults with different unit prices remain in separate rows.
	const adultGroupsMap = new Map<number, TAdultGroup>()

	adultItems.forEach((item) => {
		const key = item.roundedMoney.amount
		const existing = adultGroupsMap.get(key)

		if (existing) {
			existing.count += item.count
		} else {
			adultGroupsMap.set(key, {
				roundedMoney: item.roundedMoney,
				count: item.count,
			})
		}
	})

	// Sort adult groups by price in descending order.
	const adultGroups = Array.from(adultGroupsMap.values()).sort(
		(a, b) => b.roundedMoney.amount - a.roundedMoney.amount
	)

	// Format each adult group row (for example: "• *5,000 /-* *Per Adult* x *5 Pax*").
	const adultRows = adultGroups.map(
		(group) =>
			`• *${formatMoneyByIntl(group.roundedMoney, {
				roundOff: true,
				showSolidus: true,
			})}* *Per Adult* x *${group.count} Pax*`
	)

	type TChildGroup = {
		roundedMoney: TMoney
		count: number
		ages: Array<number>
	}

	// Group child passengers by unit price.
	// Children with the same unit price are grouped together into a single row.
	// Children with different unit prices remain in separate rows.
	const childGroupsMap = new Map<number, TChildGroup>()

	childItems.forEach((item) => {
		const key = item.roundedMoney.amount
		const existing = childGroupsMap.get(key)
		const itemAges = item.ages.length > 0 ? item.ages : []

		if (existing) {
			existing.count += item.count
			existing.ages.push(...itemAges)
		} else {
			childGroupsMap.set(key, {
				roundedMoney: item.roundedMoney,
				count: item.count,
				ages: [...itemAges],
			})
		}
	})

	// Sort child groups by price in descending order.
	const childGroups = Array.from(childGroupsMap.values()).sort(
		(a, b) => b.roundedMoney.amount - a.roundedMoney.amount
	)

	// Format each child group row.
	// If child ages are present:
	// - For multiple children, show count and sorted ages (for example: "2 (11y, 8y)").
	// - For a single child, show "1 Child (11y)".
	// If child ages are not present, show pluralized count (for example: "2 Children").
	const childContent = childGroups.map((group) => {
		const formattedPrice = formatMoneyByIntl(group.roundedMoney, {
			roundOff: true,
			showSolidus: true,
		})

		// Sort child ages in descending order (for example: 11y, 8y).
		const sortedAges = [...group.ages].sort((a, b) => b - a)
		const agesStr = sortedAges.map((age) => `${age}y`).join(", ")

		const childCountLabel =
			sortedAges.length > 0
				? group.count > 1
					? `${group.count} (${agesStr})`
					: `1 Child (${agesStr})`
				: pluralize("Child", group.count, true)

		return `• *${formattedPrice}* *Per Child* x *${childCountLabel}*`
	})

	// Combine adult rows and child rows.
	const allocationContent = [...adultRows, ...childContent].join("\n")

	const displayTitle = title
		? title.charAt(0).toUpperCase() + title.slice(1)
		: "Price"

	const displayCurrency =
		given_currency || tripQuote.given_quote?.given_currency

	return allocationContent
		? `\n*${displayTitle}${displayCurrency ? ` (${displayCurrency})` : ""}:*\n${allocationContent}`
		: ""
}
function getContentPerPersonOptionWise({
	given_currency,
	show = "hotels",
	config,
	groupedAllocations,
}: {
	given_currency?: string
	show?: TAllocationPriceType
	config: {
		tripQuote: ITripQuote
		allocation?: TAllocation[]
		quote?: IQuote
		convertMoneyToPackageCurrency?: (money: TMoney | Array<TMoney>) => TMoney
	}
	groupedAllocations?: TGroupedAllocation[]
}) {
	// Visas are fixed per trip and do not vary by option. Show visas price only once.
	if (show === "visas") {
		const content = getContentForPerPerson({
			given_currency,
			show,
			config,
		})

		return content ? `${content}\n` : ""
	}

	const groups =
		groupedAllocations && groupedAllocations.length > 0
			? groupedAllocations
			: [
					{
						title: "price",
						allocation: config.allocation || [],
						tripQuote: config.tripQuote,
						multiplier: config.tripQuote?.multipliers,
						options: [],
					},
				]

	return groups
		.map((group) => {
			const configData = {
				...config,
				allocation: group.allocation,
				tripQuote: group.tripQuote,
			}

			const content = getContentForPerPerson({
				given_currency,
				show,
				title: group.title,
				config: configData,
			})

			return content ? `${content}\n` : ""
		})
		.join("")
}

type TGroupedAllocation = {
	title: string
	allocation: ReturnType<typeof allocatePax>["allocation"]
	options: Array<string | number>
	tripQuote: ITripQuote
	multiplier: ITripQuote["multipliers"]
}

function groupAllocationsByConfiguration(
	allocationsByOption: TAllocationsByOption,
	allOptions: Array<ITripQuote>
): Array<TGroupedAllocation> {
	const groups = new Map<string, TGroupedAllocation>()

	allOptions.forEach((option, index) => {
		const allocation = allocationsByOption[option.id]?.allocation

		if (!allocation) {
			return
		}

		// here creating a unique key for each allocation configuration based on the per_pax_label and total_pax_label. This will help in grouping the allocations with the same configuration together.
		const configurationKey = allocation
			.map((item) => ({
				label: item.per_pax_label,
				total: item.total_pax_label,
			}))
			.map((item) => JSON.stringify(item))
			.sort()
			.join("|")

		const optionName =
			option.name && typeof option.name === "string" && option.name.trim()
				? option.name.trim()
				: `Option ${index + 1}`

		const existingGroup = groups.get(configurationKey)

		if (existingGroup) {
			existingGroup.options.push(optionName)
		} else {
			groups.set(configurationKey, {
				title: "price",
				allocation,
				options: [optionName],
				tripQuote: option,
				multiplier: option.multipliers,
			})
		}
	})

	return Array.from(groups.values()).map((group) => ({
		...group,
		title:
			allOptions.length > 1 ? `Price (${group.options.join(", ")})` : "Price",
	}))
}
